import React, { useMemo, useState } from 'react';
import {
  CLOCK_PRESETS,
  DEFAULT_CLOCK_CONFIG,
  describeClockConfig,
  normalizeClockConfig,
} from '../game/logic/chessClock';

const configsMatch = (a, b) => {
  const first = normalizeClockConfig(a);
  const second = normalizeClockConfig(b);
  return Object.keys(first).every((key) => first[key] === second[key]);
};

const ClockSetup = ({ initialConfig = DEFAULT_CLOCK_CONFIG, onStart, onImportPosition }) => {
  const [config, setConfig] = useState(() => normalizeClockConfig(initialConfig));
  const [stageEnabled, setStageEnabled] = useState(() => config.stageAfterMoves > 0);
  const activePreset = useMemo(
    () => CLOCK_PRESETS.find((preset) => configsMatch(preset, config))?.id ?? null,
    [config],
  );

  const updateField = (field, value) => {
    setConfig((current) => ({ ...current, [field]: value }));
  };

  const choosePreset = (preset) => {
    const next = normalizeClockConfig(preset);
    setConfig(next);
    setStageEnabled(next.stageAfterMoves > 0);
  };

  const toggleStage = (enabled) => {
    setStageEnabled(enabled);
    setConfig((current) => ({
      ...current,
      stageAfterMoves: enabled ? current.stageAfterMoves || 40 : 0,
      stageAddMinutes: enabled ? current.stageAddMinutes || 30 : 0,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onStart(normalizeClockConfig({
      ...config,
      stageAfterMoves: stageEnabled ? config.stageAfterMoves : 0,
      stageAddMinutes: stageEnabled ? config.stageAddMinutes : 0,
    }));
  };

  return (
    <div className="clock-setup-backdrop">
      <section className="clock-setup" role="dialog" aria-modal="true" aria-labelledby="clock-setup-title">
        <div className="clock-setup__intro">
          <span className="clock-setup__eyebrow">New local game</span>
          <h1 id="clock-setup-title">Set the clock</h1>
          <p>Choose how much thinking time each player receives.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <fieldset className="preset-fieldset">
            <legend>Quick presets</legend>
            <div className="clock-presets">
              {CLOCK_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  className={activePreset === preset.id ? 'clock-preset clock-preset--active' : 'clock-preset'}
                  type="button"
                  aria-pressed={activePreset === preset.id}
                  onClick={() => choosePreset(preset)}
                >
                  <span>{preset.label}</span>
                  <strong>{preset.detail}</strong>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="custom-clock-fields">
            <legend>Custom control</legend>
            <label>
              <span>Base time</span>
              <span className="number-input">
                <input
                  type="number"
                  min="1"
                  max="180"
                  step="1"
                  value={config.initialMinutes}
                  onChange={(event) => updateField('initialMinutes', event.target.value)}
                  aria-label="Base time in minutes"
                />
                <small>min</small>
              </span>
            </label>
            <label>
              <span>After each move</span>
              <span className="number-input">
                <input
                  type="number"
                  min="0"
                  max="60"
                  step="1"
                  value={config.incrementSeconds}
                  onChange={(event) => updateField('incrementSeconds', event.target.value)}
                  aria-label="Increment in seconds"
                />
                <small>sec</small>
              </span>
            </label>
          </fieldset>

          <div className="stage-control">
            <label className="stage-toggle">
              <span>
                <strong>Add a second time period</strong>
                <small>Award extra time once a player reaches a move count.</small>
              </span>
              <input
                type="checkbox"
                checked={stageEnabled}
                onChange={(event) => toggleStage(event.target.checked)}
                aria-label="Enable additional time after a number of moves"
              />
              <i aria-hidden="true" />
            </label>

            {stageEnabled && (
              <div className="stage-fields">
                <label>
                  <span>After move</span>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    step="1"
                    value={config.stageAfterMoves}
                    onChange={(event) => updateField('stageAfterMoves', event.target.value)}
                    aria-label="Move number for additional time"
                  />
                </label>
                <span aria-hidden="true">+</span>
                <label>
                  <span>Add minutes</span>
                  <input
                    type="number"
                    min="1"
                    max="180"
                    step="1"
                    value={config.stageAddMinutes}
                    onChange={(event) => updateField('stageAddMinutes', event.target.value)}
                    aria-label="Additional minutes at move number"
                  />
                </label>
              </div>
            )}
          </div>

          <div className="clock-setup__footer">
            <div className="clock-summary" aria-live="polite">
              <span>Selected control</span>
              <strong>{describeClockConfig({
                ...config,
                stageAfterMoves: stageEnabled ? config.stageAfterMoves : 0,
                stageAddMinutes: stageEnabled ? config.stageAddMinutes : 0,
              })}</strong>
            </div>
            <button className="start-game-button" type="submit">
              Start game
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
            </button>
          </div>
          <button className="clock-setup__import" type="button" onClick={onImportPosition}>
            Have a position? Import FEN instead
          </button>
        </form>
      </section>
    </div>
  );
};

export default ClockSetup;
